import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { ManageOrganizationService } from "../organization/manage-organization.service";
import { UserDetailsService } from "../user-details.service";

export type ProductTourStepId =
  | "company"
  | "member"
  | "task"
  | "project"
  | "complete";

export interface ProductTourStep {
  id: ProductTourStepId;
  route: string;
  selector: string;
  title: string;
  body: string;
  actionLabel: string;
  secondaryBody?: string;
  featurePoints?: Array<{
    icon: string;
    iconClass: string;
    label: string;
    description: string;
  }>;
  completionItems?: Array<{
    icon: string;
    iconClass: string;
    label: string;
    description: string;
  }>;
  footerNote?: string;
  isNavigationStep?: boolean;
  autoAdvanceOnTargetClick?: boolean;
  navigateTo?: string;
  followUpStep?: ProductTourStep;
}

interface ProductTourGuideContext {
  isOwner?: boolean;
  isAdmin?: boolean;
  selectedCompanyId?: number;
  selectedCompanyOwnerId?: number;
}

@Injectable({
  providedIn: "root",
})
export class ProductTourService {
  private readonly autoStartDismissedSessionKey =
    "product-tour-auto-start-dismissed";
  private isContinuing = false;
  private isOpeningStep = false;
  private stepFloor: ProductTourStepId | null = null;
  private tourGuideContext: ProductTourGuideContext | null = null;
  private readonly stepOrder: ProductTourStepId[] = [
    "company",
    "member",
    "task",
    "project",
    "complete",
  ];
  private readonly steps: ProductTourStep[] = [
    {
      id: "company",
      route: "/organization",
      selector: "[data-tour='add-company']",
      title: "Add a company",
      body: "Create your first company. The rest of the setup will happen inside that company.",
      actionLabel: "I added a company",
      isNavigationStep: true,
      autoAdvanceOnTargetClick: true,
      followUpStep: {
        id: "company",
        route: "/organization",
        selector: "[data-tour='add-company-details']",
        title: "Enter company details ",
        body: "Complete your company profile by adding basic business information. You can update these details anytime from the company settings.",
        actionLabel: "I added a company",
      },
    },
    {
      id: "member",
      route: "/organization/company-details/manage-members",
      selector: "[data-tour='add-member-details'], [data-tour='add-member']",
      title: "Add team members ",
      body: "Invite your team members, assign their roles, and set permissions to start managing tasks and projects collaboratively.",
      actionLabel: "I added members",
    },
    {
      id: "task",
      route: "/task/list",
      selector: "[data-tour='add-task-top'], [data-tour='add-task']",
      title: "Create your first task",
      body: "You're all set! Now it's time to create and assign your first task. Tasks help your team stay organized, track progress, and ensure work gets completed on time.",
      actionLabel: "I added a task",
      isNavigationStep: true,
      autoAdvanceOnTargetClick: true,
      followUpStep: {
        id: "task",
        route: "/task/list",
        selector: "[data-tour='add-task-details']",
        title: "Create Your First Task",
        body: "",
        actionLabel: "I added a task",
        featurePoints: [
          {
            icon: "T",
            iconClass: "tour-point__icon--blue",
            label: "Task Name",
            description: "What needs to be done",
          },
          {
            icon: "calendar_today",
            iconClass: "tour-point__icon--green",
            label: "Due Date",
            description: "When it should be completed",
          },
          {
            icon: "person_outline",
            iconClass: "tour-point__icon--purple",
            label: "Assignee",
            description: "Who is responsible",
          },
        ],
        footerNote: "Click Submit to get started.",
      },
    },
    {
      id: "project",
      route: "/project",
      selector: "[data-tour='add-project']",
      title: "Create a project",
      body: "Group multiple tasks under a project to manage work more efficiently and track overall progress.",
      actionLabel: "I added a project",
      isNavigationStep: true,
      autoAdvanceOnTargetClick: true,
      followUpStep: {
        id: "project",
        route: "/project",
        selector: "[data-tour='add-project-details']",
        title: "Create Your First Project",
        body: "",
        actionLabel: "I added a project",
        featurePoints: [
          {
            icon: "T",
            iconClass: "tour-point__icon--blue",
            label: "Project Name & Description",
            description: "What you're working on",
          },
          {
            icon: "calendar_today",
            iconClass: "tour-point__icon--green",
            label: "Timeline",
            description: "When it should be started and completed",
          },
          {
            icon: "group",
            iconClass: "tour-point__icon--purple",
            label: "Team Members",
            description: "Who is involved",
          },
        ],
        footerNote: "Click Add Project to get started.",
      },
    },
  ];

  activeStep$ = new BehaviorSubject<ProductTourStep | null>(null);
  isRunning$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private organizationService: ManageOrganizationService,
    private userDetailsService: UserDetailsService
  ) {}

  async startTour() {
    this.isRunning$.next(true);
    this.stepFloor = null;
    await this.openFirstIncompleteStep();
  }

  async autoStartTourIfIncomplete() {
    if (this.isRunning$.value || this.activeStep$.value) {
      return;
    }

    if (this.isAutoStartDismissedForSession()) {
      return;
    }

    const isProductTourEnabled =
      await this.userDetailsService.isProductTourEnabled();
    if (!isProductTourEnabled) {
      return;
    }

    const nextStepId = await this.getFirstIncompleteStepId();
    if (nextStepId === "complete") {
      return;
    }

    await this.startTour();
  }

  async resumeTourAfterOnboardingFlow() {
    const nextStepId = await this.getFirstIncompleteStepId();
    if (nextStepId === "complete") {
      return;
    }

    this.isRunning$.next(true);
    await this.openFirstIncompleteStep(this.stepFloor);
  }

  dismissAutoStartForSession() {
    sessionStorage.setItem(this.autoStartDismissedSessionKey, "true");
  }

  private isAutoStartDismissedForSession(): boolean {
    return (
      sessionStorage.getItem(this.autoStartDismissedSessionKey) === "true"
    );
  }

  stopTour() {
    this.activeStep$.next(null);
    this.isRunning$.next(false);
    this.stepFloor = null;
  }

  async disableTourPermanently() {
    this.dismissAutoStartForSession();
    await this.userDetailsService.disableProductTour();
    this.stopTour();
  }

  async handleRouteChange() {
    const currentStep = this.activeStep$.value;
    if (
      currentStep?.id === "member" &&
      currentStep.selector === "[data-tour='open-company']" &&
      this.router.url.startsWith("/organization/company-details")
    ) {
      await this.openFirstIncompleteStep(this.stepFloor);
    }
  }

  async continueTour() {
    if (this.isContinuing) {
      return;
    }

    this.isContinuing = true;
    const currentStep = this.activeStep$.value;
    try {
      if (currentStep?.isNavigationStep) {
        this.activeStep$.next(null);
        if (
          currentStep.navigateTo &&
          !this.router.url.startsWith(currentStep.navigateTo)
        ) {
          await this.router.navigate([currentStep.navigateTo]);
        }
        this.activeStep$.next(currentStep.followUpStep || null);
        return;
      }

      const minimumStepId = currentStep
        ? this.getNextStepId(currentStep.id)
        : null;
      this.stepFloor = this.clampStepForward(
        minimumStepId || "company",
        this.stepFloor
      );
      this.activeStep$.next(null);
      if (!minimumStepId || minimumStepId === "complete") {
        await this.openFirstIncompleteStep(minimumStepId);
      } else {
        await this.openSpecificStep(minimumStepId);
      }
    } finally {
      this.isContinuing = false;
    }
  }

  async reopenCompanyEntryStep() {
    this.activeStep$.next(null);
    await this.openSpecificStep("company");
  }

  async reopenMemberEntryStep() {
    this.activeStep$.next(null);
    await this.openSpecificStep("member");
  }

  async reopenTaskEntryStep() {
    this.activeStep$.next(null);
    await this.openSpecificStep("task");
  }

  async reopenProjectEntryStep() {
    this.activeStep$.next(null);
    await this.openSpecificStep("project");
  }

  private async openSpecificStep(stepId: ProductTourStepId): Promise<void> {
    const step = this.steps.find((item) => item.id === stepId);
    if (!step) {
      this.stopTour();
      return;
    }

    const resolvedStep =
      step.id === "member" ? this.resolveMemberStep(step) : this.resolveStep(step);

    this.stepFloor = this.clampStepForward(stepId, this.stepFloor);

    if (!this.router.url.startsWith(resolvedStep.route)) {
      const navigationStep = this.getNavigationStep(resolvedStep);
      if (navigationStep) {
        this.activeStep$.next(navigationStep);
        return;
      }

      await this.router.navigate([resolvedStep.route]);
    }

    this.activeStep$.next(resolvedStep);
  }

  private async openFirstIncompleteStep(
    minimumStepId: ProductTourStepId | null = null
  ): Promise<ProductTourStepId | null> {
    if (this.isOpeningStep) {
      return null;
    }

    this.isOpeningStep = true;
    const detectedStepId = await this.getFirstIncompleteStepId();
    const floor = this.clampStepForward(
      minimumStepId || detectedStepId,
      this.stepFloor
    );
    const effectiveStepId = this.clampStepForward(detectedStepId, floor);
    try {
      if (effectiveStepId === "complete") {
        this.stepFloor = "complete";
        this.activeStep$.next({
          id: "complete",
          route: this.router.url,
          selector: "",
          title: "Great job! 🎉",
          body: "Your TaskOPad workspace is now set up and ready for your team.",
          secondaryBody:
            "Create more tasks, manage projects, collaborate with team members, and track progress all from one place.",
          actionLabel: "Start Working",
          completionItems: [
            {
              icon: "business",
              iconClass: "tour-point__icon--blue",
              label: "Company",
              description: "Configured",
            },
            {
              icon: "group",
              iconClass: "tour-point__icon--green",
              label: "Team",
              description: "Added",
            },
            {
              icon: "assignment",
              iconClass: "tour-point__icon--purple",
              label: "Task",
              description: "Created",
            },
            {
              icon: "folder_open",
              iconClass: "tour-point__icon--amber",
              label: "Project",
              description: "Created",
            },
          ],
        });
        return "complete";
      }

      const step = this.steps.find((item) => item.id === effectiveStepId);
      if (!step) {
        this.stopTour();
        return null;
      }

      this.stepFloor = this.clampStepForward(effectiveStepId, this.stepFloor);

      const resolvedStep =
        step.id === "member" ? this.resolveMemberStep(step) : this.resolveStep(step);

      if (!this.router.url.startsWith(resolvedStep.route)) {
        const navigationStep = this.getNavigationStep(resolvedStep);
        if (navigationStep) {
          this.activeStep$.next(navigationStep);
          return effectiveStepId;
        }
        await this.router.navigate([resolvedStep.route]);
        this.activeStep$.next(resolvedStep);
        return effectiveStepId;
      }

      this.activeStep$.next(resolvedStep);
      return effectiveStepId;
    } finally {
      this.isOpeningStep = false;
    }
  }

  private resolveMemberStep(step: ProductTourStep): ProductTourStep {
    if (this.router.url.startsWith(step.route)) {
      return step;
    }

    if (this.router.url.startsWith("/organization/company-details")) {
      return {
        ...step,
        route: "/organization/company-details",
        selector:
          "[data-tour='company-members-view-all-action'], [data-tour='company-members-view-all']",
        title: "Open members",
        body: "Use the Members card to open the full member list for this company.",
        actionLabel: "View members",
        isNavigationStep: true,
        autoAdvanceOnTargetClick: true,
        navigateTo: step.route,
        followUpStep: step,
      };
    }

    return {
      ...step,
      route: "/organization",
      selector: "[data-tour='open-company']",
      title: "Open the company",
      body: "Select the company card first. The next step will take you to the company member list.",
      actionLabel: "I opened the company",
    };
  }

  private getNavigationStep(step: ProductTourStep): ProductTourStep | null {
    if (step.id === "company" || step.id === "member") {
      return this.getOrganizationNavigationStep(step);
    }

    const navigationTargets: Partial<Record<ProductTourStepId, ProductTourStep>> = {
      task: {
        id: step.id,
        route: this.router.url,
        selector: "[data-tour='nav-tasks']",
        title: "Go to tasks",
        body: "Open Tasks from the left menu. The next step will show where to add a task.",
        actionLabel: "Go to tasks",
        autoAdvanceOnTargetClick: true,
      },
      project: {
        id: step.id,
        route: this.router.url,
        selector: "[data-tour='nav-projects']",
        title: "Go to projects",
        body: "Open Projects from the left menu. The next step will show where to create a project.",
        actionLabel: "Go to projects",
        autoAdvanceOnTargetClick: true,
      },
    };

    const navigationStep = navigationTargets[step.id];
    if (!navigationStep) {
      return null;
    }

    return {
      ...navigationStep,
      isNavigationStep: true,
      navigateTo: step.route,
      followUpStep: step,
    } as ProductTourStep;
  }

  private getOrganizationNavigationStep(step: ProductTourStep): ProductTourStep {
    return {
      id: step.id,
      route: this.router.url,
      selector: "[data-tour='header-profile-menu']",
      title: "Open profile menu",
      body: "Click your name and avatar in the top-right to open the account menu.",
      actionLabel: "Menu opened",
      isNavigationStep: true,
      autoAdvanceOnTargetClick: true,
      followUpStep: {
        id: step.id,
        route: this.router.url,
        selector: "[data-tour='header-organization-management']",
        title: "Open Organization Management",
        body: "Select Organization Management from the dropdown menu.",
        actionLabel: "Open organization",
        isNavigationStep: true,
        autoAdvanceOnTargetClick: true,
        navigateTo: "/organization",
        followUpStep: step,
      },
    };
  }

  private async getFirstIncompleteStepId(): Promise<ProductTourStepId> {
    const counts = await this.getCompanyCounts();
    if (!counts.length) {
      console.log("[ProductTour] step decision", {
        reason: "no companies found",
        nextStep: "company",
      });
      return "company";
    }

    const activeCompany = this.getActiveCompanyCount(counts);
    const isOwner = this.tourGuideContext?.isOwner === true;
    const isAdmin = this.tourGuideContext?.isAdmin === true;

    if (!activeCompany) {
      console.log("[ProductTour] step decision", {
        reason: "no active company found",
        nextStep: "company",
      });
      return "company";
    }

    if (isOwner) {
      if (this.toNumber(activeCompany?.count?.memberCount) <= 1) {
        console.log("[ProductTour] step decision", {
          activeCompany,
          reason: "owner and member count <= 1",
          nextStep: "member",
        });
        return "member";
      }
    } else if (isAdmin) {
      if (this.toNumber(activeCompany?.count?.memberCount) <= 1) {
        console.log("[ProductTour] step decision", {
          activeCompany,
          reason: "admin and member count <= 1",
          nextStep: "member",
        });
        return "member";
      }
    }

    if (this.toNumber(activeCompany?.count?.taskCount) < 1) {
      console.log("[ProductTour] step decision", {
        activeCompany,
        reason: "task count < 1",
        nextStep: "task",
      });
      return "task";
    }

    if (this.toNumber(activeCompany?.count?.projectCount) < 1) {
      console.log("[ProductTour] step decision", {
        activeCompany,
        reason: "project count < 1",
        nextStep: "project",
      });
      return "project";
    }

    console.log("[ProductTour] step decision", {
      activeCompany,
      reason: "all setup complete",
      nextStep: "complete",
    });
    return "complete";
  }

  private async getCompanyCounts(): Promise<any[]> {
    const userId = this.userDetailsService.userId;
    const selectedCompanyId = this.getSelectedCompanyId() || null;
    const counts = await this.getCountList(() =>
      this.organizationService.getUnifiedCompanyCountList(
        userId,
        true,
        selectedCompanyId
      )
    );
    console.log("[ProductTour] company counts", {
      tourGuideContext: this.tourGuideContext,
      selectedCompanyId,
      counts,
    });
    return counts;
  }

  private async getCountList(fetcher: () => Promise<any>): Promise<any[]> {
    try {
      const result: any = await firstValueFrom(await fetcher());
      if (Array.isArray(result)) {
        return result;
      }

      if (result?.tourGuide) {
        this.tourGuideContext = {
          ...this.tourGuideContext,
          ...result.tourGuide,
        };
      }

      if (Array.isArray(result?.companyCounts)) {
        return result.companyCounts;
      }

      return [];
    } catch (_error) {
      return [];
    }
  }

  private getActiveCompanyCount(counts: any[]) {
    const selectedCompanyId = this.getSelectedCompanyId();
    console.log("[ProductTour] resolving active company", {
      selectedCompanyId,
      counts,
    });
    if (selectedCompanyId) {
      const selectedCompany = counts.find(
        (item) => this.toNumber(item?.companyId) === selectedCompanyId
      );
      if (selectedCompany) {
        console.log("[ProductTour] matched selected company", selectedCompany);
        return selectedCompany;
      }
    }

    console.log("[ProductTour] falling back to first company", counts[0]);
    return counts[0];
  }

  private getSelectedCompanyId(): number {
    const rawCompany = localStorage.getItem("currentCompany");
    console.log("[ProductTour] currentCompany localStorage", rawCompany);
    return this.toNumber(rawCompany);
  }

  private resolveStep(step: ProductTourStep): ProductTourStep {
    if (step.id !== "member" || this.getSelectedCompanyId()) {
      return step;
    }

    console.log("[ProductTour] resolving member step without selected company");
    return {
      ...step,
      route: "/organization",
      selector: "[data-tour='open-company']",
      title: "Open a company",
      body: "Open a company first, then go to members and invite at least one more person.",
      actionLabel: "I opened a company",
    };
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getNextStepId(stepId: ProductTourStepId): ProductTourStepId {
    const index = this.stepOrder.indexOf(stepId);
    if (index < 0 || index >= this.stepOrder.length - 1) {
      return "complete";
    }

    return this.stepOrder[index + 1];
  }

  private clampStepForward(
    detectedStepId: ProductTourStepId,
    minimumStepId: ProductTourStepId | null
  ): ProductTourStepId {
    if (!minimumStepId) {
      return detectedStepId;
    }

    const detectedIndex = this.stepOrder.indexOf(detectedStepId);
    const minimumIndex = this.stepOrder.indexOf(minimumStepId);
    if (detectedIndex < 0 || minimumIndex < 0) {
      return detectedStepId;
    }

    return detectedIndex < minimumIndex ? minimumStepId : detectedStepId;
  }
}
